/*
Viz Prop Caching Service

This Service is responsible for maintaining viz Props cache for each genNos of the answers that created.
This helps in restoring the viz prop state from the undo implementations.

*/

import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import {
    ChartVizProps,
    HeadlineVizProperties,
    TableVizProps,
} from '../services/generated/graphql-types';

const logger = create('viz-prop-cache-service');

/*
structure: {
    [answerId]: {
        visualization: {
            // can be of type chart, table or headline
            [vizId]: {
                [genNo] : vizProps
            }
        }
    }
}
*/
let vizPropCache: any = {};

const readFromVizPropCache = (
    answerId: string,
    vizId: string,
    answerSessionId: string,
    genNo: string,
) => {
    const vizProp = _.get(
        vizPropCache,
        [answerId, vizId, answerSessionId, genNo],
        null,
    );
    if (_.isNil(vizProp)) {
        logger.log(
            'Viz Prop Cache Miss! Try to read vizProp for answerId: vizId: genNo',
            answerId,
            vizId,
            answerSessionId,
            genNo,
        );
    }
    // returns a copy to make sure the original object is not modified
    return _.cloneDeep(vizProp);
};

const writeToVizPropCache = (
    answerId: string,
    vizId: string,
    answerSessionId: string,
    genNo: string,
    vizProp: ChartVizProps | TableVizProps | HeadlineVizProperties,
) => {
    if (
        _.isNil(answerId) ||
        _.isNil(vizId) ||
        _.isNil(answerSessionId) ||
        _.isNil(genNo)
    ) {
        logger.log(
            'Undefined values for answerId: answerSessionId: vizId: genNo',
            answerId,
            answerSessionId,
            vizId,
            genNo,
        );
        return;
    }
    logger.log(
        'Add/update Viz Prop cache for answerId: answerSessionId: vizId: genNo',
        answerId,
        answerSessionId,
        vizId,
        genNo,
    );
    // store a copy of the viz prop to make sure updates in chart object does not affect the cache
    _.set(
        vizPropCache,
        [answerId, vizId, answerSessionId, genNo],
        _.cloneDeep(vizProp),
    );
};

const evictCacheByAnswerId = (answerId: string) => {
    logger.log('Evicted Viz Prop cache for answer Id', answerId);
    delete vizPropCache[answerId];
};

const clearVizPropCache = () => {
    vizPropCache = {};
};

export {
    readFromVizPropCache,
    writeToVizPropCache,
    evictCacheByAnswerId,
    clearVizPropCache,
};
